import React, { useRef, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import Avatar from '../components/Avatar';
import BillingDetails from '../components/BillingDetails';

// A person's own account page. Both roles land here.
//
// A coach gets three cards: their photo, their password, their details.
// An admin gets two: their password and their details. An admin has no
// profile photo anywhere in this app on purpose (see backend routes/profile.js),
// so that card is simply not drawn for them rather than shown empty.
//
// One page for both roles was the cheaper answer than a separate admin
// account screen holding an identical password form. The admin needed
// somewhere to change their password just as much as a coach did — more so,
// in fact, since the admin is on whatever ADMIN_PASSWORD was typed into
// Coolify's environment and that value is still sat in that config.

// Checked in the browser as well as on the server. A coach on mobile data
// should be told straight away, not after waiting to upload 30 MB.
//
// The SERVER is what actually enforces this — MAX_UPLOAD_BYTES in
// backend/src/services/avatar.js. This is the courtesy check in front of it.
// If one of the two ever changes, change both.
const MAX_MB = 12;
const MAX_BYTES = MAX_MB * 1024 * 1024;

// What the file picker offers. This is a convenience, not a check — the real
// check is the server reading the file's actual contents. A phone will happily
// hand over a HEIC regardless of what is listed here.
const ACCEPT = 'image/jpeg,image/png,image/gif,image/bmp,.jpg,.jpeg,.png,.gif,.bmp';

// Matches MIN_PASSWORD_LENGTH in backend/src/routes/auth.js. The SERVER is
// what enforces it; this only exists so somebody is told before they wait for
// a round trip. If one changes, change both.
const MIN_PASSWORD = 8;

const Profile = () => {
  const { user, myPhoto, setMyPhoto, applyNewToken, clearMustChangePassword } = useAuth();
  const isCoach = user.role === 'coach';
  // Still on a password somebody else chose. Everything else in the portal is
  // closed to them until this form has been used once, so the page drops to the
  // one card that matters.
  const mustChange = user.must_change_password === true;
  const fileInput = useRef(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // The password card keeps its own messages. Sharing them with the photo
  // card would mean an upload error sat above the password form, or a
  // "Photo saved" that looks like it is telling you your password worked.
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  const pick = () => {
    setError(''); setSuccess('');
    fileInput.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    // Clear the input straight away so choosing the SAME file again still
    // fires a change event. Without this, a coach who picks a photo, gets an
    // error, fixes it and picks the same filename sees nothing happen.
    e.target.value = '';
    if (!file) return;

    setError(''); setSuccess('');

    if (file.size > MAX_BYTES) {
      setError(
        `That photo is ${(file.size / 1048576).toFixed(1)} MB. The largest we can take is ${MAX_MB} MB. ` +
        'Most phones let you choose a smaller size, or you can crop it and try again.'
      );
      return;
    }
    if (file.size === 0) {
      setError('That file is empty. Please choose a photo.');
      return;
    }

    if (busy) return;   // guards a double tap on a phone
    setBusy(true);
    try {
      const body = new FormData();
      body.append('photo', file);
      // No Content-Type set by hand. The browser has to add the multipart
      // boundary itself, and setting the header manually is what breaks it.
      const res = await api.put('/api/profile/photo', body);
      setMyPhoto(res.data);
      setSuccess(myPhoto.has_photo ? 'Photo replaced.' : 'Photo saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'The photo could not be uploaded. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    setError(''); setSuccess('');
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.delete('/api/profile/photo');
      setMyPhoto(res.data);
      setSuccess('Photo removed.');
    } catch (err) {
      setError(err.response?.data?.error || 'The photo could not be removed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');

    // Caught here, in the browser, before anything is sent. The two new
    // passwords not matching is a typing mistake, not something the server
    // should be asked about — and the server cannot tell the difference
    // anyway, it only ever receives one of them.
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwError('Please fill in all three boxes.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('The two new passwords do not match. Please type the new one again.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD) {
      setPwError(`Your new password must be at least ${MIN_PASSWORD} characters. Three random words is an easy way to get there.`);
      return;
    }
    if (newPassword === currentPassword) {
      setPwError('That is the password you are already using. Please choose a different one.');
      return;
    }

    if (pwBusy) return;   // guards a double tap on a phone
    setPwBusy(true);
    try {
      const res = await api.post('/api/auth/change-password', { currentPassword, newPassword });
      // Changing the password invalidates every token issued before it, and
      // this browser is holding one of those. The server hands back a
      // replacement so this device stays signed in; put it away before
      // anything else on the page makes another request.
      applyNewToken(res.data.token);
      // They have now chosen their own password, which is the only thing the
      // forced-change gate was waiting for. Released here rather than by asking
      // the server again: the request that just succeeded is the one that cleared
      // the column.
      clearMustChangePassword();
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setShowPasswords(false);
      setPwSuccess(
        'Password changed. You are still signed in on this device, and anyone signed in as you ' +
        'anywhere else has been signed out. Use the new password next time you log in.'
      );
    } catch (err) {
      setPwError(err.response?.data?.error || 'Your password could not be changed. Please try again.');
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <>
      <h1>{isCoach ? 'My profile' : 'My account'}</h1>

      {mustChange && (
        <div className="notice-msg">
          You are signed in with a password somebody at the club set up for you. Choose one of your own
          below and the rest of the portal will open up. Nobody else should know your password, and
          nobody at the club will ever ask you for it.
        </div>
      )}

      {isCoach && !mustChange && (
        <div className="card">
          <h2>Profile photo</h2>
          {error && <div className="error-msg">{error}</div>}
          {success && <div className="success-msg">{success}</div>}

          <div className="profile-photo-row">
            <Avatar name={user.name} src={myPhoto.large} size="lg" />

            <div className="profile-photo-actions">
              <p className="muted" style={{ marginTop: 0 }}>
                {myPhoto.has_photo
                  ? 'This is the photo shown next to your name.'
                  : 'You have not added a photo. Your initials are shown next to your name instead. A photo is optional.'}
              </p>

              <input
                ref={fileInput}
                type="file"
                accept={ACCEPT}
                onChange={onFileChosen}
                style={{ display: 'none' }}
              />

              <div className="profile-photo-buttons">
                <button className="btn" type="button" onClick={pick} disabled={busy}>
                  {busy ? 'Working…' : (myPhoto.has_photo ? 'Replace photo' : 'Upload a photo')}
                </button>
                {myPhoto.has_photo && (
                  <button className="btn secondary" type="button" onClick={remove} disabled={busy}>
                    Remove photo
                  </button>
                )}
              </div>

              <p className="muted">
                A JPEG or PNG, up to {MAX_MB} MB. It is cropped to a square automatically,
                so a normal photo of your face works fine.
              </p>

              <p className="muted">
                Your photo just helps club admins put a face to your name in the portal.
                Only you and club admins can see it, and you can change or remove it
                whenever you like.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Change my password</h2>
        {pwError && <div className="error-msg">{pwError}</div>}
        {pwSuccess && <div className="success-msg">{pwSuccess}</div>}

        <p className="muted" style={{ marginTop: 0 }}>
          If you are still using a password somebody at the club set up for you, change it here.
          Nobody else should know your password, and nobody at the club will ever ask you for it.
        </p>

        <form onSubmit={changePassword} className="password-form">
          {/* Chrome needs a username on a change-password form to know which
              saved login it is updating. Without it, it either ignores the
              change or saves a second, orphaned entry. It is off-screen rather
              than hidden or display:none, because several password managers
              skip fields hidden those two ways. Required by the house rule of
              10/09/2026. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={user.email}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="offscreen-username"
          />

          <div className="form-row">
            <label htmlFor="current-password">Current password</label>
            <input
              id="current-password"
              name="currentPassword"
              type={showPasswords ? 'text' : 'password'}
              autoComplete="current-password"
              value={currentPassword}
              onChange={e => { setCurrentPassword(e.target.value); setPwError(''); }}
            />
          </div>

          <div className="form-row">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              name="newPassword"
              type={showPasswords ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setPwError(''); }}
            />
          </div>

          <div className="form-row">
            <label htmlFor="confirm-password">Type the new password again</label>
            <input
              id="confirm-password"
              name="confirmPassword"
              type={showPasswords ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setPwError(''); }}
            />
          </div>

          {/* Worth having on a phone in a sports hall, where a mistyped
              password you cannot see is the usual reason this form fails. */}
          <label className="muted password-show">
            <input
              type="checkbox"
              checked={showPasswords}
              onChange={e => setShowPasswords(e.target.checked)}
            />
            Show what I am typing
          </label>

          <button className="btn" type="submit" disabled={pwBusy}>
            {pwBusy ? 'Changing…' : 'Change password'}
          </button>
        </form>

        <p className="muted" style={{ marginBottom: 0 }}>
          At least {MIN_PASSWORD} characters. Three random words you will remember beats
          something short and complicated. Changing your password signs you out everywhere
          else you are logged in.
        </p>
      </div>

      {/* Coaches only, and not while they are still on somebody else's
          password: the one job on that screen is to replace it. */}
      {isCoach && !mustChange && <BillingDetails />}

      {!mustChange && (
        <div className="card">
          <h2>My details</h2>
          <table>
            <tbody>
              <tr><th style={{ width: '9rem' }}>Name</th><td>{user.name}</td></tr>
              <tr><th>Email</th><td>{user.email}</td></tr>
            </tbody>
          </table>
          <p className="muted" style={{ marginBottom: 0 }}>
            {isCoach
              ? 'Ask an admin if any of this needs changing.'
              : 'Your account was set up when the portal was installed. Ask your developer if this needs changing.'}
          </p>
        </div>
      )}
    </>
  );
};

export default Profile;
